import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Package, Calendar, CreditCard, LogOut, Trash2, ExternalLink, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PortalPage = () => {
  const [orders, setOrders] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({ gamerTag: '', preferredPlatform: 'Xbox' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total_count: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchUserData(token);
    fetchOrders(token, currentPage);
  }, [navigate, currentPage]);

  const fetchUserData = async (token) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });
      setUser(response.data.user);
      setProfileData({
        gamerTag: response.data.user.gamer_tag || '',
        preferredPlatform: response.data.user.preferred_platform || 'Xbox'
      });
    } catch (error) {
      console.error('Error fetching user:', error);
      localStorage.removeItem('token');
      navigate('/login');
    }
  };

  const handleSaveProfile = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${BACKEND_URL}/api/auth/profile`, {
        gamer_tag: profileData.gamerTag,
        preferred_platform: profileData.preferredPlatform
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });
      await fetchUserData(token);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile');
    }
  };

  const fetchOrders = async (token, page = 1) => {
    try {
      setError(null);
      const response = await axios.get(`${BACKEND_URL}/api/orders?page=${page}&limit=25`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });
      setOrders(response.data.orders);
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to load orders. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleDeleteOrder = async (orderId) => {
    setDeleting(true);
    const token = localStorage.getItem('token');
    
    try {
      await axios.delete(`${BACKEND_URL}/api/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove order from local state
      setOrders(orders.filter(o => o.order_id !== orderId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting order:', error);
      alert(error.response?.data?.detail || 'Failed to delete order');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'text-green-500 border-green-500/30 bg-green-900/20';
      case 'Pending': return 'text-yellow-500 border-yellow-500/30 bg-yellow-900/20';
      case 'Paid': return 'text-blue-500 border-blue-500/30 bg-blue-900/20';
      default: return 'text-gray-500 border-gray-500/30 bg-gray-900/20';
    }
  };

  if (loading) {
    return (
      <div className="app-content bg-[#050505] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500 mx-auto mb-4"></div>
          <div className="text-white text-xl">Loading your orders...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-content bg-[#050505] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">{error}</div>
          <button 
            onClick={() => window.location.reload()}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 uppercase tracking-wider"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-content bg-[#050505] min-h-screen">
      <Navbar />
      
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
            <div>
              <h1 className="text-4xl md:text-5xl font-black uppercase mb-2" data-testid="portal-title">
                <span className="text-red-500">CUSTOMER</span> PORTAL
              </h1>
              <p className="text-gray-400 font-['Rajdhani'] text-lg">
                Welcome back, {user?.email}
              </p>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="mt-4 md:mt-0 bg-transparent border border-red-500 hover:bg-red-500 text-white py-3 px-6 uppercase tracking-wider transition-all flex items-center gap-2"
            >
              <LogOut size={20} />
              LOGOUT
            </button>
          </div>

          {/* Profile Section */}
          <div className="bg-[#0A0A0A] border border-white/10 p-8 mb-12">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold uppercase text-white">MY PROFILE</h2>
              <button
                onClick={() => isEditing ? handleSaveProfile() : setIsEditing(true)}
                className="bg-red-600 hover:bg-red-700 text-white py-2 px-6 uppercase tracking-wider text-sm transition-all"
              >
                {isEditing ? 'SAVE' : 'EDIT'}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm uppercase tracking-wider text-gray-400 mb-2 block">Email</label>
                <input
                  type="text"
                  value={user?.email || ''}
                  disabled
                  className="w-full bg-black/40 border border-white/10 text-gray-500 h-12 px-4 font-['Rajdhani']"
                />
              </div>
              <div>
                <label className="text-sm uppercase tracking-wider text-gray-400 mb-2 block">Gamer Tag</label>
                <input
                  type="text"
                  value={profileData.gamerTag}
                  onChange={(e) => setProfileData({...profileData, gamerTag: e.target.value})}
                  disabled={!isEditing}
                  className="w-full bg-black border border-white/10 focus:border-red-500 text-white h-12 px-4 font-['Rajdhani'] disabled:bg-black/40 disabled:text-gray-500"
                  placeholder="Enter your gamer tag"
                />
              </div>
              <div>
                <label className="text-sm uppercase tracking-wider text-gray-400 mb-2 block">Preferred Platform</label>
                <select
                  value={profileData.preferredPlatform}
                  onChange={(e) => setProfileData({...profileData, preferredPlatform: e.target.value})}
                  disabled={!isEditing}
                  className="w-full bg-black border border-white/10 focus:border-red-500 text-white h-12 px-4 font-['Rajdhani'] disabled:bg-black/40 disabled:text-gray-500"
                >
                  <option value="Xbox">Xbox</option>
                  <option value="PlayStation">PlayStation</option>
                  <option value="PC">PC</option>
                </select>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <div className="bg-[#0A0A0A] border border-white/10 p-6" data-testid="stats-total-orders">
              <Package className="text-red-500 mb-4" size={32} />
              <h3 className="text-3xl font-black text-white mb-2">{orders.length}</h3>
              <p className="text-gray-400 uppercase tracking-wider text-sm">Total Orders</p>
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 p-6" data-testid="stats-pending">
              <Calendar className="text-yellow-500 mb-4" size={32} />
              <h3 className="text-3xl font-black text-white mb-2">
                {orders.filter(o => o.status === 'Pending').length}
              </h3>
              <p className="text-gray-400 uppercase tracking-wider text-sm">Pending</p>
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 p-6" data-testid="stats-paid">
              <CreditCard className="text-blue-500 mb-4" size={32} />
              <h3 className="text-3xl font-black text-white mb-2">
                {orders.filter(o => o.status === 'Paid').length}
              </h3>
              <p className="text-gray-400 uppercase tracking-wider text-sm">Paid (In Progress)</p>
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 p-6" data-testid="stats-completed">
              <CheckCircle className="text-green-500 mb-4" size={32} />
              <h3 className="text-3xl font-black text-white mb-2">
                {orders.filter(o => o.status === 'Completed').length}
              </h3>
              <p className="text-gray-400 uppercase tracking-wider text-sm">Completed</p>
            </div>
          </div>

          {/* Active Boosts Section (Paid orders - in progress) */}
          {orders.filter(o => o.status === 'Paid').length > 0 && (
            <div className="mb-12">
              <h2 className="text-3xl font-bold uppercase mb-8 text-white" data-testid="active-boosts-title">
                ACTIVE BOOSTS (IN PROGRESS)
              </h2>
              <div className="space-y-6">
                {orders.filter(o => o.status === 'Paid').map((order, index) => (
                  <div
                    key={index}
                    className="bg-[#0A0A0A] border border-blue-500/30 p-6 hover:border-blue-500/50 transition-all"
                    data-testid={`active-boost-${index}`}
                  >
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-white">{order.product_name}</h3>
                          <span className="px-3 py-1 text-xs uppercase tracking-wider border text-blue-500 border-blue-500/30 bg-blue-900/20">
                            PAID - IN PROGRESS
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-400">
                          <div>
                            <span className="text-gray-500">Gamer Tag:</span> {order.gamer_tag}
                          </div>
                          <div>
                            <span className="text-gray-500">Platform:</span> {order.platform}
                          </div>
                          <div>
                            <span className="text-gray-500">Paid:</span>{' '}
                            {order.paid_at ? new Date(order.paid_at).toLocaleDateString() : new Date(order.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-black text-blue-500">{order.product_price}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Orders List */}
          <div>
            <h2 className="text-3xl font-bold uppercase mb-8 text-white" data-testid="order-history-title">
              ORDER HISTORY
            </h2>
            
            {orders.length === 0 ? (
              <div className="bg-[#0A0A0A] border border-white/10 p-12 text-center">
                <Package className="text-gray-600 mx-auto mb-4" size={64} />
                <p className="text-gray-400 text-lg">No orders yet. Start shopping!</p>
              </div>
            ) : (
              <div className="space-y-6">
                {orders.map((order, index) => (
                  <div
                    key={index}
                    className="bg-[#0A0A0A] border border-white/10 p-6 hover:border-red-500/30 transition-all"
                    data-testid={`order-${index}`}
                  >
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-white">{order.product_name}</h3>
                          <span className={`px-3 py-1 text-xs uppercase tracking-wider border ${getStatusColor(order.status)}`}>
                            {order.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-400">
                          <div>
                            <span className="text-gray-500">Gamer Tag:</span> {order.gamer_tag}
                          </div>
                          <div>
                            <span className="text-gray-500">Platform:</span> {order.platform}
                          </div>
                          <div>
                            <span className="text-gray-500">Date:</span>{' '}
                            {new Date(order.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="mt-2 text-sm text-gray-400">
                          <span className="text-gray-500">Availability:</span> {order.availability}
                        </div>
                        {order.status === 'Pending' && order.expires_at && (
                          <div className="mt-2 text-xs text-yellow-500">
                            ⏰ Expires: {new Date(order.expires_at).toLocaleString()}
                          </div>
                        )}
                      </div>
                      <div className="text-right flex flex-col items-end gap-3">
                        <p className="text-3xl font-black text-red-500">{order.product_price}</p>
                        {order.status === 'Pending' && (
                          <div className="flex flex-col gap-2">
                            {/* Complete Payment button */}
                            <a
                              href={`${order.stripe_url}${order.stripe_url.includes('?') ? '&' : '?'}client_reference_id=${order.order_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-green-600 hover:bg-green-700 text-white transition-colors flex items-center gap-2 text-sm px-4 py-2 uppercase tracking-wider"
                            >
                              <ExternalLink size={16} />
                              <span>Complete Payment</span>
                            </a>
                            {/* Delete button */}
                            <button
                              onClick={() => setDeleteConfirm(order)}
                              className="text-red-500 hover:text-red-400 transition-colors flex items-center gap-2 text-sm justify-end"
                              title="Delete Order"
                            >
                              <Trash2 size={16} />
                              <span>Delete</span>
                            </button>
                          </div>
                        )}
                        {order.status === 'Completed' && (
                          <span className="text-green-500 text-xs flex items-center gap-1">
                            <CheckCircle size={14} />
                            Completed {order.completed_at ? `on ${new Date(order.completed_at).toLocaleDateString()}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination Controls */}
            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-between mt-6 bg-black/40 border border-white/10 p-4">
                <div className="text-gray-400 text-sm">
                  Showing page {pagination.page} of {pagination.total_pages} ({pagination.total_count} total orders)
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={!pagination.has_prev}
                    className={`flex items-center gap-1 px-4 py-2 uppercase tracking-wider text-sm transition-all ${
                      pagination.has_prev 
                        ? 'bg-red-600 hover:bg-red-700 text-white' 
                        : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={!pagination.has_next}
                    className={`flex items-center gap-1 px-4 py-2 uppercase tracking-wider text-sm transition-all ${
                      pagination.has_next 
                        ? 'bg-red-600 hover:bg-red-700 text-white' 
                        : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 px-4">
          <div className="bg-[#0A0A0A] border border-red-500/50 p-8 max-w-md w-full">
            <h3 className="text-2xl font-bold text-white mb-4 uppercase">CONFIRM DELETE</h3>
            <p className="text-gray-400 mb-4">
              Are you sure you want to delete this pending order?
            </p>
            <div className="bg-black/40 border border-white/10 p-4 mb-6 text-sm">
              <p className="text-gray-400"><span className="text-white">Product:</span> {deleteConfirm.product_name}</p>
              <p className="text-gray-400"><span className="text-white">Price:</span> {deleteConfirm.product_price}</p>
              <p className="text-gray-400"><span className="text-white">Status:</span> {deleteConfirm.status}</p>
            </div>
            <p className="text-yellow-500 text-sm mb-6 bg-yellow-900/20 border border-yellow-500/30 p-3">
              ⚠️ This action cannot be undone. You can only delete pending orders.
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => handleDeleteOrder(deleteConfirm.order_id)}
                disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 uppercase tracking-wider transition-all disabled:opacity-50"
              >
                {deleting ? 'DELETING...' : 'DELETE'}
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 uppercase tracking-wider transition-all disabled:opacity-50"
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default PortalPage;